// Generated by Claude 3.7 Sonnet Thinking

import { Node } from "./Node";
import { type Container } from "../Dom/Container";
import { traverseTreeRecursive } from "@/map/utils/TreeUtils";

/**
 * 渲染管理器 - 用于统一管理渲染流程和优化
 */
export class RenderManager {
    // 单例实例
    private static instance: RenderManager;
    /**
     * 渲染队列 - 存储需要渲染的节点
     * @remark 
     * 使用 Set 来存储节点，保证每个节点在队列中只存在一次，避免重复渲染
     */
    private renderQueue: Set<Node> = new Set();
    // 动画帧 ID - 用于 requestAnimationFrame
    private animationFrameId?: number;
    // 是否正在渲染 - 用于避免重复渲染
    private isRendering: boolean = false;

    /**
     * 获取单例实例
     * @returns RenderManager 实例
     */
    public static getInstance(): RenderManager {
        if (!RenderManager.instance) {
            RenderManager.instance = new RenderManager();
        }
        return RenderManager.instance;
    }

    private constructor() {
        // 私有构造函数，防止外部直接创建实例
    }

    /**
     * 将节点添加到渲染队列
     * @param node 需要渲染的节点
     */
    public queueForRender(node: Node): void {
        this.renderQueue.add(node);
        this.scheduleRender();
    }

    /**
     * 批量将节点添加到渲染队列
     * @param nodes 需要渲染的节点数组
     */
    public queueMultiple(nodes: Node[]): void {
        nodes.forEach(node => this.renderQueue.add(node));
        this.scheduleRender();
    }

    /**
     * 从渲染队列中移除节点
     * @param node 要移除的节点
     */
    public unqueueFromRender(node: Node): void {
        this.renderQueue.delete(node);
    }

    /**
     * 安排渲染（使用requestAnimationFrame进行优化）
     */
    private scheduleRender(): void {
        if (!this.animationFrameId && !this.isRendering) {
            this.animationFrameId = requestAnimationFrame(() => this.render());
        }
    }

    /**
     * 执行实际渲染
     */
    private render(): void {
        this.animationFrameId = undefined;
        this.isRendering = true;

        // 按照层级排序节点，确保父节点先于子节点渲染(level越小越先渲染)
        const sortedNodes = Array.from(this.renderQueue).sort((a, b) => a.level - b.level);

        // 执行渲染
        sortedNodes.forEach(node => {
            if (this.renderQueue.has(node)) {
                node.render();
                this.renderQueue.delete(node);
            }
        });

        this.isRendering = false;

        // 在渲染过程中，如果有新的节点加入队列，则重新安排渲染
        if (this.renderQueue.size > 0) {
            this.scheduleRender();
        }
    }

    /**
     * 立即渲染指定节点（不经过队列）
     * @param node 要立即渲染的节点
     */
    public renderImmediate(node: Node): void {
        node.render();
    }

    /**
     * 立即渲染所有队列中的节点
     */
    public flushRender(): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = undefined;
        }
        this.render();
    }

    /**
     * 清空渲染队列
     */
    public clearQueue(): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = undefined;
        }
        this.renderQueue.clear();
    }

    /**
     * 管理容器 - 将容器添加到渲染队列
     * @param container 要管理的容器
     */
    public manageContainer(container: Container): void {
        // 将容器添加到渲染队列
        this.queueForRender(container);
    }

    /**
     * 批量渲染容器内的所有节点
     * @param container 容器
     */
    public renderContainer(container: Container): void {
        const allNodes = this.collectAllNodes(container);
        this.queueMultiple(allNodes);
        this.flushRender();
    }

    /**
     * 收集容器中的所有节点（包括嵌套容器中的节点）
     * @param container 容器
     * @returns 所有节点数组
     */
    private collectAllNodes(container: Container): Node[] {
        const result: Node[] = [];
        // 使用 TreeUtils 中的 traverseTreeRecursive 进行前序遍历来收集所有节点
        // 由于节点树是异构的 (Container 有 children，而普通 Node 没有),
        // 但 traverseTreeRecursive 的泛型约束 T extends { children?: T[] } 期望一个同构树
        // 因此我们这里使用 `any` 类型来灵活处理这种异构性。
        // 在运行时，`traverseTreeRecursive` 内部的 `node.children?.length` 可以正确处理没有 children 属性的节点
        traverseTreeRecursive<any>(container, (node: Node) => {
            result.push(node);
        });
        return result;
    }
}